import { useCallback, useEffect, useMemo, useState } from 'react'
import { Award, FileText } from 'lucide-react'
import Button from '../../components/Button'
import DataTable from '../../components/DataTable'
import DatePicker from '../../components/DatePicker'
import FilterBar from '../../components/FilterBar'
import { InfoNote } from '../../components/Form'
import { useOnChange, useRemembered } from '../../components/listMemory'
import PageHeader from '../../components/PageHeader'
import Select from '../../components/Select'
import { useToast } from '../../components/toastContext'
import useListParams from '../../components/useListParams'
import { downloadCertificates, downloadUsersList, getPrintPage, listIssuedTo } from './printService'
import { strings as S } from './strings'
import './print.css'

/**
 * What this screen keeps in its own address — the programme and the two dates.
 *
 * They were plain state, which made the one screen you would send to a colleague
 * unaddressable: `/print-certificate` always opened on "choose a programme", and the
 * choice was lost again on every Back. `?program=7&from=2026-01-01` IS the certificates
 * that programme issued since January. Defaults stay out of the URL.
 */
const LIST_PARAMS = { program: '', from: '', to: '' }

/**
 * طباعة الشهادة — the legacy `/PrintCertificate`.
 *
 * Built rather than wired (R5): the route rendered a placeholder, so the nav entry and the
 * legacy URL both led to a screen that did nothing. The nav entry stays HIDDEN, as it is in
 * the legacy layout — this is reached by its URL and by the sidebar only when unhidden.
 *
 * Two downloads, and they are not the same thing: one produces the CERTIFICATES, the other
 * the ROSTER of who they were printed for.
 */
export default function PrintCertificatePage() {
  const toast = useToast()

  const [programs, setPrograms] = useState([])
  /* Which certificates are being looked at — in the address, so the view survives a
     refresh and can be sent to someone. See components/useListParams.js. */
  const [params, setParams] = useListParams(LIST_PARAMS)
  const programId = params.program
  const fromDate = params.from
  const toDate = params.to
  const setProgramId = useCallback((next) => setParams({ program: next }), [setParams])
  const setFromDate = useCallback((next) => setParams({ from: next }), [setParams])
  const setToDate = useCallback((next) => setParams({ to: next }), [setParams])

  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(false)
  const [note, setNote] = useState('')
  /* The ticked rows are the whole job of this screen — a print run is a dozen people
     picked out of a long list — and they were plain state, so leaving and coming back
     meant ticking all of them again. */
  const [selected, setSelected] = useRemembered('print-certificate:selected', [])
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    getPrintPage()
      .then((page) => setPrograms(page.programs))
      .catch((error) => toast.error(error?.message || S.actionFailed))
  }, [toast])

  /* Nothing is fetched until a programme is chosen — the endpoint is keyed by one, and
     asking without it returns a refusal rather than everything. */
  useEffect(() => {
    if (!programId) {
      setRows([])
      setNote('')
      return undefined
    }

    let cancelled = false
    setLoading(true)

    listIssuedTo({ entityId: programId, fromDate, toDate })
      .then((response) => {
        if (cancelled) return
        setRows(response.rows)
        /* The server's own sentence when a programme has issued none — shown in place of
           an empty table with no explanation. */
        setNote(response.rows.length === 0 ? response.message : '')
        setLoading(false)
      })
      .catch((error) => {
        if (cancelled) return
        setRows([])
        setNote('')
        setLoading(false)
        toast.error(error?.message || S.actionFailed)
      })

    return () => {
      cancelled = true
    }
  }, [programId, fromDate, toDate, toast])

  /* The ticks belong to the list that was on screen, so a new programme or a new range
     drops them. It CHANGES the selection, it does not clear it on arrival — this used to
     run inside the load effect, which fires on mount and would wipe what was just
     restored. See useOnChange. */
  useOnChange(() => setSelected([]), [programId, fromDate, toDate])

  const chosen = useMemo(
    () => rows.filter((row) => selected.includes(row.id)),
    [rows, selected],
  )

  const program = useMemo(
    () => programs.find((option) => String(option.value) === String(programId)) ?? null,
    [programs, programId],
  )

  const columns = useMemo(
    () => [
      { key: 'username', header: S.columns.username, render: (row) => <bdi>{row.username}</bdi> },
      { key: 'name', header: S.columns.name, render: (row) => <bdi>{row.name}</bdi> },
      {
        key: 'email',
        header: S.columns.email,
        render: (row) => (
          <span dir="ltr" className="print-cert__mail">
            {row.email}
          </span>
        ),
      },
      { key: 'group', header: S.columns.group, render: (row) => <bdi>{row.group}</bdi> },
      {
        key: 'issuedOn',
        header: S.columns.issuedOn,
        render: (row) => (
          <span dir="ltr" className="print-cert__date">
            {row.issuedOn}
          </span>
        ),
      },
      {
        key: 'expiresOn',
        header: S.columns.expiresOn,
        render: (row) => (
          <span dir="ltr" className="print-cert__date">
            {row.expiresOn}
          </span>
        ),
      },
      {
        key: 'daysLeft',
        header: S.columns.daysLeft,
        /* The text can be a word rather than a number, so the raw span does the sorting. */
        value: (row) => row.daysLeftValue,
        render: (row) => <bdi>{row.daysLeft}</bdi>,
      },
    ],
    [],
  )

  const run = async (call) => {
    setBusy(true)
    const outcome = await call
    setBusy(false)
    if (!outcome.ok) toast.error(outcome.message || S.actionFailed)
  }

  return (
    <>
      <PageHeader
        title={S.title}
        subtitle={S.subtitle}
        count={loading ? undefined : rows.length}
        actions={
          <>
            <Button
              disabled={busy || chosen.length === 0}
              onClick={() =>
                run(downloadCertificates({ entityId: programId, userIds: chosen.map((row) => row.id) }))
              }
            >
              <Award size={16} strokeWidth={1.75} />
              {S.downloadCertificates}
            </Button>

            <Button
              variant="soft"
              disabled={busy || chosen.length === 0}
              onClick={() => run(downloadUsersList({ program, users: chosen }))}
            >
              <FileText size={16} strokeWidth={1.75} />
              {S.downloadList}
            </Button>
          </>
        }
      />

      <FilterBar>
        <Select
          value={programId}
          onChange={setProgramId}
          options={programs}
          placeholder={S.filter.program}
          aria-label={S.filter.program}
        />
        <DatePicker value={fromDate} onChange={setFromDate} placeholder={S.filter.from} />
        <DatePicker value={toDate} onChange={setToDate} placeholder={S.filter.to} />
      </FilterBar>

      {!programId && (
        <InfoNote icon={Award}>
          <bdi>{S.chooseProgram}</bdi>
        </InfoNote>
      )}

      {note && (
        <InfoNote icon={Award}>
          <bdi>{note}</bdi>
        </InfoNote>
      )}

      <DataTable
        columns={columns}
        rows={rows}
        loading={loading}
        controls={false}
        /* The programme and the dates are the filter, and they sit above the table where
           it cannot see them — so page 4 of one programme's roster stayed page 4 when
           another programme with two pages was picked. */
        resetKey={`${programId}|${fromDate}|${toDate}`}
        getRowId={(row) => row.id}
        onRowClick={(row) =>
          setSelected((previous) =>
            previous.includes(row.id)
              ? previous.filter((id) => id !== row.id)
              : [...previous, row.id],
          )
        }
        isRowSelected={(row) => selected.includes(row.id)}
        emptyMessage={S.empty}
      />
    </>
  )
}
